import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../auth";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PortalHeader } from "../components/PortalHeader";
import { TcmDatePicker } from "../components/TcmDatePicker";

export function InviteClientPage() {
  const { API, authHeaders } = useAuth();
  const nav = useNavigate();
  const [full_name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await axios.post(`${API}/admin/invite-client`, { email, full_name }, { headers: authHeaders });
      toast.success("Invite sent");
      nav("/admin");
    } catch (err) { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen" data-testid="invite-client-page">
      <PortalHeader eyebrow="Invite client" backTo="/admin" />
      <div className="p-10 max-w-lg mx-auto">
      <h1 className="font-display text-4xl">Invite client</h1>
      <hr className="tcm-gold-rule w-16 my-6" />
      <form onSubmit={submit} className="space-y-5">
        <div><label className="tcm-eyebrow block mb-2">Full name</label>
          <input data-testid="client-name" className="tcm-input" required value={full_name} onChange={(e) => setName(e.target.value)} /></div>
        <div><label className="tcm-eyebrow block mb-2">Email</label>
          <input data-testid="client-email" type="email" className="tcm-input" required value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <button data-testid="invite-submit" disabled={busy} className="tcm-btn-primary w-full">{busy ? "Sending..." : "Send invitation"}</button>
      </form>
      </div>
    </div>
  );
}

export function NewTripPage() {
  const { API, authHeaders } = useAuth();
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [params] = useSearchParams();
  const prefilledClientId = params.get("client_id") || "";
  const [form, setForm] = useState({ client_id: prefilledClientId, destination: "", start_date: "", end_date: "", timezone: "Europe/London", currency: "EUR", party_size: 1 });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/clients`, { headers: authHeaders }).then((r) => setClients(r.data));
    axios.get(`${API}/admin/templates`, { headers: authHeaders }).then((r) => setTemplates(r.data)).catch(() => {});
  }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      let data;
      if (templateId) {
        const res = await axios.post(`${API}/admin/trips-from-template`,
          { ...form, template_id: templateId }, { headers: authHeaders });
        data = res.data;
      } else {
        const res = await axios.post(`${API}/admin/trips`, form, { headers: authHeaders });
        data = res.data;
      }
      toast.success("Trip created");
      nav(`/admin/trips/${data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const applyTemplate = (id) => {
    setTemplateId(id);
    const tpl = templates.find((t) => t.id === id);
    if (tpl && !form.destination) setForm((f) => ({ ...f, destination: tpl.source_destination || tpl.name }));
  };
  return (
    <div className="min-h-screen" data-testid="new-trip-page">
      <PortalHeader eyebrow="New trip" backTo="/admin" />
      <div className="p-10 max-w-lg mx-auto">
      <h1 className="font-display text-4xl">New trip</h1>
      <hr className="tcm-gold-rule w-16 my-6" />

      {templates.length > 0 && (
        <div className="mb-6">
          <label className="tcm-eyebrow block mb-2">Start from</label>
          <div className="space-y-2">
            <label className={`block cursor-pointer border p-3 ${templateId === "" ? "border-[color:var(--tcm-navy)] bg-[color:var(--tcm-navy)] text-[color:var(--tcm-cream)]" : "border-[color:var(--tcm-navy-15)]"}`}>
              <input data-testid="tpl-blank" type="radio" name="tpl" className="sr-only"
                     checked={templateId === ""} onChange={() => setTemplateId("")} />
              <p className="font-display text-lg">Blank trip</p>
              <p className="text-[11px] opacity-70">Fill everything in from scratch</p>
            </label>
            {templates.map((t) => (
              <label key={t.id} data-testid={`tpl-${t.id}`}
                     className={`block cursor-pointer border p-3 ${templateId === t.id ? "border-[color:var(--tcm-navy)] bg-[color:var(--tcm-navy)] text-[color:var(--tcm-cream)]" : "border-[color:var(--tcm-navy-15)]"}`}>
                <input type="radio" name="tpl" className="sr-only"
                       checked={templateId === t.id} onChange={() => applyTemplate(t.id)} />
                <p className="font-display text-lg">{t.name}</p>
                <p className="text-[11px] opacity-70">
                  {t.counts?.itinerary || 0} plan · {t.counts?.reservations || 0} bookings · {t.counts?.budget_lines || 0} lines · {t.counts?.logistics || 0} logistics · {t.counts?.recommendations || 0} recs
                </p>
              </label>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-5">
        <div><label className="tcm-eyebrow block mb-2">Client</label>
          <select data-testid="trip-client" className="tcm-input" required value={form.client_id} onChange={set("client_id")}>
            <option value="">Select client</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name} · {c.email}</option>)}
          </select></div>
        <div><label className="tcm-eyebrow block mb-2">Destination</label>
          <input data-testid="trip-destination" className="tcm-input" required value={form.destination} onChange={set("destination")} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="tcm-eyebrow block mb-2">Start</label>
            <TcmDatePicker testId="trip-start" value={form.start_date} onChange={(v) => setForm({...form, start_date: v})} /></div>
          <div><label className="tcm-eyebrow block mb-2">End</label>
            <TcmDatePicker testId="trip-end" value={form.end_date} onChange={(v) => setForm({...form, end_date: v})} min={form.start_date || undefined} /></div>
        </div>
        <div><label className="tcm-eyebrow block mb-2">Currency</label>
          <select data-testid="trip-currency" className="tcm-input" value={form.currency} onChange={set("currency")}>
            <option>EUR</option><option>GBP</option>
          </select></div>
        <div><label className="tcm-eyebrow block mb-2">Number of guests</label>
          <input data-testid="trip-party-size" type="number" min="1" max="30" className="tcm-input"
                 value={form.party_size}
                 onChange={(e) => setForm({ ...form, party_size: parseInt(e.target.value || "1", 10) })} /></div>
        <button data-testid="trip-submit" disabled={busy} className="tcm-btn-primary w-full">
          {busy ? "Creating..." : templateId ? "Create trip from template" : "Create trip"}
        </button>
      </form>
      </div>
    </div>
  );
}
